import React, { useMemo, useState } from "react";
import "./ContactForm.css";
import { handleSubmit } from "../../handlers/contactHandler.ts";

type FormErrors = Partial<{
  name: string;
  email: string;
  phone: string;
  pickup: string;
  dropoff: string;
  date: string;
  time: string;
  passengers: string;
}>;

const emailRegex =
  /^(?:[a-zA-Z0-9_'^&+%!-]+(?:\.[a-zA-Z0-9_'^&+%!-]+)*)@(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;

const ContactForm: React.FC = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    pickup: "",
    dropoff: "",
    date: "",
    time: "",
    passengers: "",
    message: "",
  });
  const [timeInputs, setTimeInputs] = useState({
    hour: "",
    minutes: "",
    period: "AM",
  });
  const [status, setStatus] = useState<{
    message: string;
    type: "success" | "error";
  } | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const convertTo24Hour = (
    hour: string,
    minutes: string,
    period: string,
  ): string => {
    let h = parseInt(hour);
    if (period === "PM" && h < 12) h += 12;
    if (period === "AM" && h === 12) h = 0;
    return `${h.toString().padStart(2, "0")}:${minutes}`;
  };

  const handleTimeChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    if (name === "hour") {
      const num = parseInt(value);
      if (!isNaN(num)) {
        setTimeInputs((prev) => ({
          ...prev,
          hour: Math.min(Math.max(num, 1), 12).toString(),
        }));
      } else if (value === "") {
        setTimeInputs((prev) => ({ ...prev, hour: "" }));
      }
    } else if (name === "minutes") {
      const num = parseInt(value);
      if (!isNaN(num)) {
        setTimeInputs((prev) => ({
          ...prev,
          minutes: Math.min(Math.max(num, 0), 59).toString().padStart(2, "0"),
        }));
      } else if (value === "") {
        setTimeInputs((prev) => ({ ...prev, minutes: "" }));
      }
    } else if (name === "period") {
      setTimeInputs((prev) => ({ ...prev, period: value }));
    }
  };

  const handleTimeBlur = () => {
    const { hour, minutes, period } = timeInputs;
    if (hour && minutes) {
      setFormData((prev) => ({
        ...prev,
        time: convertTo24Hour(hour, minutes, period),
      }));
      setErrors((prev) => ({ ...prev, time: "" }));
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "name" && value.trim())
      setErrors((prev) => ({ ...prev, name: "" }));
    if (name === "email" && emailRegex.test(value))
      setErrors((prev) => ({ ...prev, email: "" }));
    if (name === "phone" && value.trim())
      setErrors((prev) => ({ ...prev, phone: "" }));
    if (name === "pickup" && value.trim())
      setErrors((prev) => ({ ...prev, pickup: "" }));
    if (name === "dropoff" && value.trim())
      setErrors((prev) => ({ ...prev, dropoff: "" }));
    if (name === "date" && value) setErrors((prev) => ({ ...prev, date: "" }));
    if (name === "passengers" && value)
      setErrors((prev) => ({ ...prev, passengers: "" }));
  };

  const validate = useMemo(() => {
    return (data = formData): FormErrors => {
      const v: FormErrors = {};
      if (!data.name.trim()) v.name = "Please enter your name";
      if (!data.email.trim()) v.email = "Please enter your email";
      else if (!emailRegex.test(data.email.trim()))
        v.email = "Please enter a valid email";
      if (!data.phone.trim()) v.phone = "Please enter your phone number";
      if (!data.date) v.date = "Please choose a date";
      if (!data.time) v.time = "Please enter a pickup time";
      return v;
    };
  }, [formData]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus(null);
    const v = validate(formData);
    setErrors(v);
    if (Object.values(v).some(Boolean)) return;

    try {
      setIsSubmitting(true);
      const result = await handleSubmit(formData);
      setStatus({
        message: result.message,
        type: result.success ? "success" : "error",
      });
      if (result.success) {
        setFormData({
          name: "",
          email: "",
          phone: "",
          pickup: "",
          dropoff: "",
          date: "",
          time: "",
          passengers: "",
          message: "",
        });
        setTimeInputs({ hour: "", minutes: "", period: "AM" });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const req = (
    <span className="required-star" aria-hidden>
      *
    </span>
  );

  return (
    <div className="contact-form-section">
      <div className="form-title">
        <span className="form-title-main">Need a ride?</span>
        <span className="form-title-sub">
          Fill out the form and we'll get back to you shortly.
        </span>
      </div>
      {status && (
        <div className={`status-message ${status.type}`} role="alert">
          {status.message}
        </div>
      )}
      <form onSubmit={onSubmit} className="contact-form" noValidate>
        {/* Trip details */}
        <div className="form-row two-col">
          <div className="form-group">
            <label htmlFor="pickup">Pickup Location</label>
            <input
              type="text"
              id="pickup"
              name="pickup"
              placeholder="Address or landmark"
              className={errors.pickup ? "input-error" : ""}
              aria-invalid={!!errors.pickup}
              aria-describedby={errors.pickup ? "pickup-error" : undefined}
              value={formData.pickup}
              onChange={handleChange}
            />
            {errors.pickup && (
              <small id="pickup-error" className="form-error">
                {errors.pickup}
              </small>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="dropoff">Drop-off Location</label>
            <input
              type="text"
              id="dropoff"
              name="dropoff"
              placeholder="Address or landmark"
              className={errors.dropoff ? "input-error" : ""}
              aria-invalid={!!errors.dropoff}
              aria-describedby={errors.dropoff ? "dropoff-error" : undefined}
              value={formData.dropoff}
              onChange={handleChange}
            />
            {errors.dropoff && (
              <small id="dropoff-error" className="form-error">
                {errors.dropoff}
              </small>
            )}
          </div>
        </div>

        <div className="form-row three-col">
          <div className="form-group">
            <label htmlFor="date">Date {req}</label>
            <input
              type="date"
              id="date"
              name="date"
              className={errors.date ? "input-error" : ""}
              aria-invalid={!!errors.date}
              aria-describedby={errors.date ? "date-error" : undefined}
              value={formData.date}
              onChange={handleChange}
            />
            {errors.date && (
              <small id="date-error" className="form-error">
                {errors.date}
              </small>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="hour">Time {req}</label>
            <div
              className={`time-input-group${errors.time ? " input-error-wrap" : ""}`}
            >
              <input
                type="number"
                min="1"
                max="12"
                id="hour"
                name="hour"
                placeholder="HH"
                value={timeInputs.hour}
                onChange={handleTimeChange}
                onBlur={handleTimeBlur}
                aria-label="Hour"
              />
              <span aria-hidden>:</span>
              <input
                type="number"
                min="0"
                max="59"
                id="minutes"
                name="minutes"
                placeholder="MM"
                value={timeInputs.minutes}
                onChange={handleTimeChange}
                onBlur={handleTimeBlur}
                aria-label="Minutes"
              />
              <select
                name="period"
                value={timeInputs.period}
                onChange={handleTimeChange}
                onBlur={handleTimeBlur}
                aria-label="AM or PM"
              >
                <option value="AM">AM</option>
                <option value="PM">PM</option>
              </select>
            </div>
            {errors.time && <small className="form-error">{errors.time}</small>}
          </div>
          <div className="form-group">
            <label htmlFor="passengers">Passengers</label>
            <select
              id="passengers"
              name="passengers"
              className={errors.passengers ? "input-error" : ""}
              aria-invalid={!!errors.passengers}
              aria-describedby={
                errors.passengers ? "passengers-error" : undefined
              }
              value={formData.passengers}
              onChange={handleChange}
            >
              <option value="">Select</option>
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            {errors.passengers && (
              <small id="passengers-error" className="form-error">
                {errors.passengers}
              </small>
            )}
          </div>
        </div>

        {/* Contact info */}
        <div className="form-row two-col">
          <div className="form-group">
            <label htmlFor="name">Name {req}</label>
            <input
              type="text"
              id="name"
              name="name"
              placeholder="Your full name"
              className={errors.name ? "input-error" : ""}
              aria-invalid={!!errors.name}
              aria-describedby={errors.name ? "name-error" : undefined}
              value={formData.name}
              onChange={handleChange}
            />
            {errors.name && (
              <small id="name-error" className="form-error">
                {errors.name}
              </small>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="phone">Phone {req}</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              placeholder="Your phone number"
              className={errors.phone ? "input-error" : ""}
              aria-invalid={!!errors.phone}
              aria-describedby={errors.phone ? "phone-error" : undefined}
              value={formData.phone}
              onChange={handleChange}
            />
            {errors.phone && (
              <small id="phone-error" className="form-error">
                {errors.phone}
              </small>
            )}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="email">Email {req}</label>
          <input
            type="email"
            id="email"
            name="email"
            placeholder="you@example.com"
            className={errors.email ? "input-error" : ""}
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? "email-error" : undefined}
            value={formData.email}
            onChange={handleChange}
          />
          {errors.email ? (
            <small id="email-error" className="form-error">
              {errors.email}
            </small>
          ) : (
            <small className="form-hint">We'll reply to this address.</small>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="message">Notes</label>
          <textarea
            id="message"
            name="message"
            placeholder="Special requests, flight number, extra stops..."
            value={formData.message}
            onChange={handleChange}
            rows={3}
          />
        </div>

        <button type="submit" className="submit-button" disabled={isSubmitting}>
          {isSubmitting ? (
            <span className="btn-content">
              <span className="spinner" aria-hidden /> Sending...
            </span>
          ) : (
            "Send Message"
          )}
        </button>
      </form>
    </div>
  );
};

export default ContactForm;
